<template>
  <!-- Painel de administração do cluster (somente leitura). Não aparece em nenhum menu:
       acesso por /admin/cluster (menu do administrador), com o login de uma conta de administrador
       (ADMIN_EMAILS) ou, em emergência, com o token ADMIN_TOKEN. -->
  <div class="admin-cluster">
    <v-app-bar app flat dense color="theme-background">
      <v-icon color="primary" class="mr-2">mdi-server-network</v-icon>
      <v-toolbar-title class="text-subtitle-1 font-weight-medium">
        ElectrosFI · Cluster
      </v-toolbar-title>
      <v-btn small text class="ml-2" to="/dashboard">
        <v-icon left small>mdi-arrow-left</v-icon> Plataforma
      </v-btn>
      <v-spacer />
      <template v-if="session">
        <span v-if="email" class="text-caption mr-3 grey--text">{{ email }}</span>
        <span class="text-caption mr-3" :class="stale ? 'warning--text' : ''">
          <v-icon x-small :color="error ? 'error' : 'primary'" class="mr-1">mdi-circle</v-icon>
          {{ lastUpdate ? `Atualizado às ${formatClock(lastUpdate)}` : "Carregando..." }}
        </span>
        <v-select
          v-model="intervalS"
          :items="intervals"
          item-text="text"
          item-value="value"
          dense
          hide-details
          outlined
          class="interval-select mr-2"
        />
        <v-btn small text @click="togglePause">
          <v-icon left small>{{ paused ? "mdi-play" : "mdi-pause" }}</v-icon>
          {{ paused ? "Retomar" : "Pausar" }}
        </v-btn>
        <v-btn small text @click="logout()">
          <v-icon left small>mdi-logout</v-icon>
          Sair
        </v-btn>
      </template>
    </v-app-bar>

    <v-main>
      <!-- Entrada: login de administrador (Firebase) ou token de emergência -->
      <v-container v-if="!session" class="fill-height">
        <v-row justify="center">
          <v-col cols="12" sm="8" md="5">
            <v-card outlined>
              <v-card-title class="text-subtitle-1">Acesso restrito</v-card-title>
              <v-card-text>
                <v-progress-linear v-if="checking" indeterminate color="primary" class="mb-3" />
                <p class="text-body-2">
                  <template v-if="denied">
                    A conta <strong>{{ denied }}</strong> não é de administrador.
                  </template>
                  <template v-else>Entre na plataforma com uma conta de administrador e abra este painel pelo menu.</template>
                </p>
                <v-btn color="primary" block to="/login" class="mb-4" :disabled="checking">Entrar na plataforma</v-btn>
                <p class="text-caption grey--text mb-1">
                  Acesso de emergência: token <code>ADMIN_TOKEN</code> do <code>cluster.env</code> (fica só nesta aba).
                </p>
                <v-form @submit.prevent="login">
                  <v-text-field
                    v-model="tokenInput"
                    label="Token de administrador"
                    type="password"
                    autocomplete="off"
                    outlined
                    dense
                    :error-messages="loginError"
                  />
                  <v-btn outlined block type="submit" :disabled="!tokenInput" :loading="loading">
                    Entrar com o token
                  </v-btn>
                </v-form>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>

      <v-container v-else fluid>
        <v-alert v-if="error" type="error" dense text class="mb-3">{{ error }}</v-alert>

        <!-- Resumo -->
        <v-row dense v-if="data">
          <v-col cols="6" md="3" v-for="card in summary" :key="card.title">
            <v-card outlined>
              <v-card-text class="py-3">
                <div class="text-caption">{{ card.title }}</div>
                <div class="text-h6">{{ card.value }}</div>
                <div class="text-caption">{{ card.detail }}</div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Nós -->
        <v-card outlined class="mt-3" v-if="data">
          <v-card-title class="text-subtitle-1">Nós</v-card-title>
          <v-alert v-if="data.nodes_error" type="warning" dense text class="mx-4">
            {{ data.nodes_error }}
          </v-alert>
          <v-data-table
            :headers="nodeHeaders"
            :items="data.nodes"
            item-key="name"
            dense
            hide-default-footer
            disable-pagination
          >
            <template v-slot:[`item.state`]="{ item }">
              <v-chip x-small label :color="nodeColor(item)" text-color="white">{{ item.state }}</v-chip>
            </template>
            <template v-slot:[`item.reason`]="{ item }">
              <span v-if="item.reason">
                {{ item.reason }}
                <span class="text-caption grey--text" v-if="item.reason_by || item.reason_time">
                  ({{ [item.reason_by, item.reason_time].filter(Boolean).join(" · ") }})
                </span>
              </span>
              <span v-else class="grey--text">—</span>
            </template>
            <template v-slot:[`item.cpus`]="{ item }">
              <div class="cpu-cell">
                <span class="text-caption">
                  {{ item.cpus_alloc }} / {{ item.cpus_total }} alocadas · {{ item.cpus_free }} livres
                </span>
                <v-progress-linear
                  :value="item.cpus_total ? (100 * item.cpus_alloc) / item.cpus_total : 0"
                  height="4"
                  color="primary"
                />
              </div>
            </template>
            <template v-slot:[`item.cpu_load`]="{ item }">
              {{ item.cpu_load == null ? "—" : item.cpu_load.toFixed(2) }}
            </template>
            <template v-slot:[`item.memory`]="{ item }">
              <span class="text-caption">
                {{ formatMemory(item.alloc_memory_mb) }} alocada ·
                {{ formatMemory(item.free_memory_mb) }} livre ·
                {{ formatMemory(item.real_memory_mb) }} total
              </span>
            </template>
            <template v-slot:[`item.partitions`]="{ item }">
              {{ item.partitions.join(", ") || "—" }}
            </template>
          </v-data-table>
        </v-card>

        <!-- Fila -->
        <v-card outlined class="mt-3" v-if="data">
          <v-card-title class="text-subtitle-1">Fila (squeue)</v-card-title>
          <v-alert v-if="data.queue_error" type="warning" dense text class="mx-4">
            {{ data.queue_error }}
          </v-alert>
          <v-data-table
            :headers="queueHeaders"
            :items="data.queue"
            item-key="slurm_job_id"
            dense
            hide-default-footer
            disable-pagination
            no-data-text="Nenhum job na fila"
            @click:row="item => item.run_id && openRun(item.run_id)"
            :item-class="item => (item.run_id ? 'clickable' : '')"
          >
            <template v-slot:[`item.solver`]="{ item }">{{ item.solver || "—" }}</template>
            <template v-slot:[`item.state`]="{ item }">
              <v-chip x-small label :color="slurmStateColor(item.state)" text-color="white">
                {{ item.state }}
              </v-chip>
            </template>
            <template v-slot:[`item.elapsed`]="{ item }">
              {{ item.elapsed || "—" }}<span class="grey--text" v-if="item.time_limit"> / {{ item.time_limit }}</span>
            </template>
            <template v-slot:[`item.where`]="{ item }">
              <span v-if="item.state === 'PENDING'" class="warning--text">{{ item.reason || "—" }}</span>
              <span v-else>{{ item.nodelist || "—" }}</span>
            </template>
          </v-data-table>
        </v-card>

        <!-- Execuções -->
        <v-card outlined class="mt-3" v-if="data">
          <v-card-title class="text-subtitle-1">
            Execuções recentes
            <span class="text-caption grey--text ml-2">(do NFS; clique para ver o detalhe e o log)</span>
            <v-spacer />
            <v-btn
              v-if="selectedRuns.length"
              small
              text
              color="error"
              @click="askDelete(selectedRuns)"
            >
              <v-icon left small>mdi-delete</v-icon> Apagar selecionadas ({{ selectedRuns.length }})
            </v-btn>
            <v-btn small text color="error" :disabled="!finishedRuns" @click="confirmDelete = { all: true, ids: [] }">
              <v-icon left small>mdi-delete-sweep</v-icon> Apagar terminadas ({{ finishedRuns }})
            </v-btn>
          </v-card-title>
          <v-data-table
            :headers="runHeaders"
            v-model="selectedRuns"
            :items="runRows"
            item-key="id"
            show-select
            selectable-key="finished"
            dense
            :items-per-page="15"
            no-data-text="Nenhuma execução registrada"
            :item-class="() => 'clickable'"
            @click:row="item => openRun(item.id)"
          >
            <template v-slot:[`item.id`]="{ item }">
              <code>{{ item.id.slice(0, 8) }}</code>
            </template>
            <template v-slot:[`item.simulation`]="{ item }">
              <div class="sim-cell">
                <div>{{ item.simulation.title || "(sem título)" }}</div>
                <div class="text-caption grey--text">
                  {{ item.simulation.author ? `autor ${shortId(item.simulation.author)}` : "manual" }}
                  <template v-if="item.simulation.simulation_id"> · sim {{ shortId(item.simulation.simulation_id) }}</template>
                </div>
              </div>
            </template>
            <template v-slot:[`item.plan`]="{ item }">
              <v-chip v-if="item.plan" x-small label outlined :title="item.plan.reason">
                {{ item.plan.complexity === "complex" ? "complexa" : "simples" }}
              </v-chip>
              <span v-else class="grey--text">—</span>
            </template>
            <template v-slot:[`item.outputs_count`]="{ item }">
              {{ item.outputs_count || "—" }}
            </template>
            <template v-slot:[`item.state`]="{ item }">
              <v-chip x-small label :color="runStateColor(item.state)" text-color="white">{{ item.state }}</v-chip>
            </template>
            <template v-slot:[`item.procs`]="{ item }">
              {{ item.ranks || "—" }} × {{ item.threads || 1 }}
            </template>
            <template v-slot:[`item.exit_code`]="{ item }">
              {{ item.exit_code == null ? "—" : item.exit_code }}
            </template>
            <template v-slot:[`item.timings`]="{ item }">
              <span class="text-caption timings">
                <span v-for="column in timingColumns" :key="column.key"
                      :class="item.timings[column.key] < 0 ? 'warning--text' : ''">
                  {{ column.short }} {{ formatDuration(item.timings[column.key]) }}
                </span>
              </span>
            </template>
            <template v-slot:[`item.submitted_at`]="{ item }">
              {{ formatDateTime(item.submitted_at) }}
            </template>
            <template v-slot:[`item.actions`]="{ item }">
              <v-btn
                icon
                x-small
                aria-label="Apagar execução"
                title="Apagar execução"
                :disabled="!isFinished(item.state)"
                @click.stop="askDelete([item])"
              >
                <v-icon small>mdi-delete-outline</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card>

        <!-- Rede -->
        <v-card outlined class="mt-3 mb-6" v-if="data">
          <v-card-title class="text-subtitle-1">Rede (ping-pong MPI mais recente)</v-card-title>
          <v-card-text v-if="!data.network">
            Nenhum resultado de ping-pong encontrado. Rode
            <code>slurm/tests/mpi-validate.sbatch</code> com 2 nós ou mais (docs/CLUSTER.md, seção 5).
          </v-card-text>
          <v-card-text v-else>
            <p class="mb-2">
              <strong>{{ data.network.node_a }}</strong> ↔ <strong>{{ data.network.node_b }}</strong>
              <v-chip
                x-small
                label
                class="ml-2"
                :color="data.network.ok === false ? 'error' : 'primary'"
                text-color="white"
              >
                {{ data.network.ok === false ? "FALHOU" : "OK" }}
              </v-chip>
              <span class="text-caption grey--text ml-2">
                {{ data.network.source }} · {{ formatDateTime(data.network.measured_at) }}
              </span>
            </p>
            <v-simple-table dense>
              <thead>
                <tr>
                  <th>Mensagem</th>
                  <th>Latência (µs)</th>
                  <th>Banda (MB/s)</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in data.network.rows" :key="row.bytes">
                  <td>{{ formatBytes(row.bytes) }}</td>
                  <td>{{ row.latency_us.toFixed(2) }}</td>
                  <td>{{ row.bandwidth_mb_s.toFixed(1) }}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-card-text>
        </v-card>
      </v-container>
    </v-main>

    <!-- Detalhe de uma execução -->
    <v-dialog v-model="detailOpen" max-width="1000" scrollable>
      <v-card v-if="detail">
        <v-card-title class="text-subtitle-1">
          Execução <code class="ml-2">{{ detail.id }}</code>
          <v-chip x-small label class="ml-2" :color="runStateColor(detail.state)" text-color="white">
            {{ detail.state }}
          </v-chip>
          <v-spacer />
          <v-btn
            small
            text
            color="error"
            class="mr-1"
            :disabled="!isFinished(detail.state)"
            :title="isFinished(detail.state) ? 'Apagar esta execução' : 'Na fila ou rodando: não pode ser apagada'"
            @click="askDelete([detail])"
          >
            <v-icon left small>mdi-delete-outline</v-icon> Apagar
          </v-btn>
          <v-btn icon small @click="detailOpen = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text>
          <p class="text-body-2 mb-2">{{ detail.message }}</p>
          <v-row dense>
            <v-col cols="12" md="6">
              <v-simple-table dense>
                <tbody>
                  <tr><td>Solver</td><td>{{ detail.solver }}</td></tr>
                  <tr><td>Job do Slurm</td><td>{{ detail.slurm_job_id || "—" }} ({{ detail.slurm_state || "—" }})</td></tr>
                  <tr><td>Nós</td><td>{{ detail.nodes || "—" }} ({{ detail.num_nodes || "—" }})</td></tr>
                  <tr><td>Processos × threads</td><td>{{ detail.ranks || "—" }} × {{ detail.threads || 1 }}</td></tr>
                  <tr><td>Tentativa</td><td>{{ detail.attempt }}</td></tr>
                  <tr><td>Código de saída</td><td>{{ detail.exit_code == null ? "—" : detail.exit_code }}</td></tr>
                  <tr><td>Recursos pedidos</td><td class="text-caption">{{ formatResources(detail.resources) }}</td></tr>
                </tbody>
              </v-simple-table>
            </v-col>
            <v-col cols="12" md="6">
              <v-simple-table dense>
                <tbody>
                  <tr v-for="column in timingRows" :key="column.key">
                    <td>{{ column.text }}</td>
                    <td :class="detail.timings[column.key] < 0 ? 'warning--text' : ''">
                      {{ formatDuration(detail.timings[column.key]) }}
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
              <p class="text-caption grey--text mt-1" v-if="hasNegativeTiming(detail.timings)">
                Valores negativos indicam relógios do mestre e dos nós fora de sincronia (confira o NTP).
              </p>
            </v-col>
          </v-row>
          <div class="text-subtitle-2 mt-3">Simulação</div>
          <v-simple-table dense>
            <tbody>
              <tr><td>Título</td><td>{{ detail.simulation.title || "(sem título)" }}</td></tr>
              <tr><td>Autor (uid)</td><td><code>{{ detail.simulation.author || "—" }}</code></td></tr>
              <tr><td>Simulação</td><td><code>{{ detail.simulation.simulation_id || "—" }}</code></td></tr>
              <tr v-if="detail.simulation.production"><td>Produção</td><td><code>{{ detail.simulation.production }}</code></td></tr>
              <tr v-if="detail.simulation.quality"><td>Preset</td><td>{{ detail.simulation.quality }}</td></tr>
              <tr v-if="detail.plan">
                <td>Plano</td>
                <td>
                  {{ detail.plan.complexity === "complex" ? "complexa" : "simples" }} ·
                  <span class="text-caption">{{ detail.plan.reason }}</span>
                </td>
              </tr>
              <tr v-for="(value, key) in detail.problem" :key="key">
                <td>{{ problemLabel(key) }}</td><td>{{ formatProblem(value) }}</td>
              </tr>
            </tbody>
          </v-simple-table>

          <div class="d-flex align-center mt-3">
            <span class="text-subtitle-2">Saídas</span>
            <v-spacer />
            <v-btn v-if="detail.results_page" x-small text color="primary" :href="detail.results_page" target="_blank">
              <v-icon left x-small>mdi-open-in-new</v-icon> Página de resultados
            </v-btn>
          </div>
          <p v-if="!detail.outputs.length" class="text-caption grey--text mb-0">
            {{ detail.state === "completed" ? "Nenhum arquivo de saída registrado." : "Sem saídas: a execução não terminou com sucesso." }}
          </p>
          <div v-else class="outputs">
            <v-chip
              v-for="output in detail.outputs"
              :key="output.url"
              small
              outlined
              class="mr-2 mb-2"
              :href="output.url"
              target="_blank"
              rel="noopener"
            >
              <v-icon left small>{{ outputIcon(output.kind) }}</v-icon>
              {{ output.name }}
            </v-chip>
          </div>
          <v-simple-table v-if="detail.resonances.length" dense class="mt-1">
            <thead>
              <tr><th>Ressonância</th><th>Frequência (c/µm)</th><th>λ (µm)</th><th>Q</th></tr>
            </thead>
            <tbody>
              <template v-for="res in detail.resonances">
                <tr v-if="!res.modes.length" :key="res.name"><td>{{ res.name }}</td><td colspan="3" class="grey--text">nenhum modo encontrado</td></tr>
                <tr v-for="(mode, index) in res.modes" :key="`${res.name}-${index}`">
                  <td>{{ index === 0 ? res.name : "" }}</td>
                  <td>{{ formatNumber(mode.frequency, 5) }}</td>
                  <td>{{ formatNumber(mode.wavelength, 4) }}</td>
                  <td>{{ formatNumber(mode.Q, 1) }}</td>
                </tr>
              </template>
            </tbody>
          </v-simple-table>

          <v-alert v-if="detail.result_error" type="error" dense text class="mt-2 result-error">
            {{ detail.result_error }}
          </v-alert>

          <div class="d-flex align-center mt-3">
            <span class="text-subtitle-2">Log do Slurm</span>
            <span class="text-caption grey--text ml-2" v-if="log && log.file">
              {{ log.file }} · {{ formatBytes(log.size) }} · {{ formatDateTime(log.modified_at) }}
            </span>
            <v-spacer />
            <v-select
              v-model="logLines"
              :items="[100, 200, 500, 1000]"
              dense
              hide-details
              outlined
              class="interval-select"
              label="Linhas"
            />
          </div>
          <pre ref="log" class="log mt-2">{{ logText }}</pre>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Confirmação antes de apagar -->
    <v-dialog :value="!!confirmDelete" max-width="480" @input="value => { if (!value) confirmDelete = null; }">
      <v-card v-if="confirmDelete">
        <v-card-title class="text-subtitle-1">
          {{ confirmDelete.all ? "Apagar todas as execuções terminadas?"
            : confirmDelete.ids.length > 1 ? `Apagar ${confirmDelete.ids.length} execuções?` : "Apagar esta execução?" }}
        </v-card-title>
        <v-card-text>
          <ul v-if="!confirmDelete.all" class="mb-2 delete-list">
            <li v-for="run in confirmDelete.runs" :key="run.id">
              <code>{{ run.id.slice(0, 8) }}</code> · {{ (run.simulation && run.simulation.title) || "(sem título)" }}
            </li>
          </ul>
          <p class="mb-2">
            Some deste painel e do resumo de uso do cluster: entrada, tempos, status e logs do Slurm. Não dá para
            desfazer.
          </p>
          <p class="mb-0">
            As simulações e os resultados salvos pelos usuários continuam. Execuções na fila ou rodando não são apagadas.
          </p>
          <v-alert v-if="deleteError" type="error" dense text class="mt-3 mb-0">{{ deleteError }}</v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="deleting" @click="confirmDelete = null">Cancelar</v-btn>
          <v-btn color="error" text :loading="deleting" @click="removeRuns">Apagar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import firebase from "firebase/app";
import { deleteRuns, fetchOverview, fetchRun, fetchRunLog, fetchWhoami } from "@/services/admin";

const TOKEN_KEY = "ELECTROSFI_ADMIN_TOKEN";

const TIMINGS = [
  { key: "queue_s", text: "Fila", short: "fila" },
  { key: "startup_s", text: "Inicialização", short: "início" },
  { key: "mesh_s", text: "Malha" },
  { key: "solver_s", text: "Solver", short: "solver" },
  { key: "postprocess_s", text: "Pós-processamento", short: "pós" },
  { key: "finalize_s", text: "Finalização" },
  { key: "delivery_s", text: "Entrega" },
  { key: "total_s", text: "Total", short: "total" }
];

const readToken = () => {
  try {
    return sessionStorage.getItem(TOKEN_KEY) || "";
  } catch (err) {
    return "";
  }
};

const storeToken = token => {
  try {
    if (token) sessionStorage.setItem(TOKEN_KEY, token);
    else sessionStorage.removeItem(TOKEN_KEY);
  } catch (err) {
    // armazenamento indisponível (modo privado): o token vale só enquanto a página estiver aberta
  }
};

export default {
  name: "AdminCluster",
  data: () => ({
    // "firebase" = login de conta de administrador; "token" = ADMIN_TOKEN (emergência); "" = fora
    session: readToken() ? "token" : "",
    adminToken: readToken(),
    email: "",
    denied: "",
    checking: false,
    tokenInput: "",
    loginError: "",
    loading: false,
    data: null,
    error: "",
    lastUpdate: null,
    paused: false,
    intervalS: 3,
    intervals: [
      { text: "2 s", value: 2 },
      { text: "3 s", value: 3 },
      { text: "5 s", value: 5 }
    ],
    timer: null,
    detailOpen: false,
    confirmDelete: null,
    selectedRuns: [],
    deleting: false,
    deleteError: "",
    detailId: null,
    detail: null,
    log: null,
    logLines: 200,
    nodeHeaders: [
      { text: "Nó", value: "name" },
      { text: "Estado", value: "state" },
      { text: "Motivo", value: "reason", sortable: false },
      { text: "CPUs", value: "cpus", sortable: false },
      { text: "Carga", value: "cpu_load" },
      { text: "Memória", value: "memory", sortable: false },
      { text: "Partições", value: "partitions", sortable: false }
    ],
    queueHeaders: [
      { text: "Job", value: "slurm_job_id" },
      { text: "Solver", value: "solver" },
      { text: "Nome", value: "name" },
      { text: "Estado", value: "state" },
      { text: "Decorrido / limite", value: "elapsed" },
      { text: "Nós", value: "nodes" },
      { text: "CPUs", value: "cpus" },
      { text: "Nós em uso / motivo da espera", value: "where", sortable: false },
      { text: "Usuário", value: "user" }
    ]
  }),
  computed: {
    // Etapas resumidas na tabela de execuções (o detalhe mostra todas)
    timingColumns() {
      return TIMINGS.filter(t => t.short);
    },
    timingRows() {
      return TIMINGS;
    },
    runHeaders() {
      return [
        { text: "Id", value: "id", sortable: false },
        { text: "Simulação", value: "simulation", sortable: false },
        { text: "Solver", value: "solver" },
        { text: "Plano", value: "plan", sortable: false },
        { text: "Estado", value: "state" },
        { text: "Nós", value: "nodes" },
        { text: "Proc. × threads", value: "procs", sortable: false },
        { text: "Tempos", value: "timings", sortable: false },
        { text: "Saídas", value: "outputs_count" },
        { text: "Submetido", value: "submitted_at" },
        { text: "", value: "actions", sortable: false, align: "end" }
      ];
    },
    // Linhas da tabela: só as terminadas podem ser selecionadas para apagar
    runRows() {
      return ((this.data && this.data.runs) || []).map(run => ({ ...run, finished: this.isFinished(run.state) }));
    },
    finishedRuns() {
      return ((this.data && this.data.runs) || []).filter(run => this.isFinished(run.state)).length;
    },
    summary() {
      const nodes = this.data.nodes || [];
      const queue = this.data.queue || [];
      const available = nodes.filter(n => n.available).length;
      const free = nodes.filter(n => n.available).reduce((sum, n) => sum + n.cpus_free, 0);
      const total = nodes.reduce((sum, n) => sum + n.cpus_total, 0);
      return [
        { title: "Nós disponíveis", value: `${available} / ${nodes.length}`, detail: this.downNodes },
        { title: "CPUs livres", value: free, detail: `de ${total} no total` },
        {
          title: "Jobs em execução",
          value: queue.filter(j => j.state === "RUNNING").length,
          detail: `${queue.filter(j => j.state === "PENDING").length} na fila`
        },
        {
          title: "Ping-pong",
          value: this.data.network ? this.networkSummary : "—",
          detail: this.data.network ? `${this.data.network.node_a} ↔ ${this.data.network.node_b}` : "sem medição"
        }
      ];
    },
    downNodes() {
      const down = (this.data.nodes || []).filter(n => !n.available).map(n => n.name);
      return down.length ? `indisponíveis: ${down.join(", ")}` : "todos disponíveis";
    },
    networkSummary() {
      const rows = this.data.network.rows;
      if (!rows.length) return "—";
      const latency = rows[0].latency_us.toFixed(1);
      const bandwidth = Math.max(...rows.map(r => r.bandwidth_mb_s));
      return `${latency} µs · ${(bandwidth / 1000).toFixed(2)} GB/s`;
    },
    stale() {
      return this.lastUpdate && Date.now() - this.lastUpdate > 4 * this.intervalS * 1000;
    },
    logText() {
      if (!this.log) return "Carregando...";
      if (!this.log.file) return "O job ainda não gravou log (fila do Slurm).";
      return (this.log.truncated ? "...\n" : "") + this.log.lines.join("\n");
    }
  },
  watch: {
    intervalS() {
      this.schedule();
    },
    logLines() {
      this.refreshDetail();
    },
    detailOpen(open) {
      if (!open) {
        this.detail = null;
        this.detailId = null;
        this.log = null;
      }
    }
  },
  methods: {
    // Credencial da próxima consulta: o token do Firebase é renovado aqui (expira em 1 h)
    async bearer() {
      if (this.session === "firebase") {
        const user = firebase.auth().currentUser;
        if (!user) throw Object.assign(new Error("Sessão encerrada"), { response: { status: 401 } });
        return user.getIdToken();
      }
      return this.adminToken;
    },
    async checkFirebaseAdmin() {
      const user = firebase.auth().currentUser;
      if (!user) return;
      this.checking = true;
      try {
        const { data } = await fetchWhoami(await user.getIdToken());
        if (data && data.admin) {
          this.session = "firebase";
          this.email = user.email || "";
          this.refresh();
        } else {
          this.denied = user.email || "atual";
        }
      } catch (err) {
        // 401/erro de rede: fica na tela de entrada (login ou token de emergência)
      } finally {
        this.checking = false;
      }
    },
    login() {
      this.loginError = "";
      this.adminToken = this.tokenInput.trim();
      this.session = "token";
      this.tokenInput = "";
      storeToken(this.adminToken);
      this.paused = false;
      this.refresh();
    },
    logout(message = "") {
      clearTimeout(this.timer);
      this.session = "";
      this.adminToken = "";
      this.email = "";
      storeToken("");
      this.data = null;
      this.lastUpdate = null;
      this.detailOpen = false;
      this.loginError = message;
    },
    togglePause() {
      this.paused = !this.paused;
      if (!this.paused) this.refresh();
      else clearTimeout(this.timer);
    },
    schedule() {
      clearTimeout(this.timer);
      if (!this.session || this.paused) return;
      this.timer = setTimeout(this.refresh, this.intervalS * 1000);
    },
    handleError(err) {
      const status = err.response && err.response.status;
      if (status === 401 || status === 403) {
        this.logout(this.session === "token" ? "Token inválido." : "Sessão de administrador encerrada.");
        return true;
      }
      if (status === 503 && err.response.data && /ADMIN_/.test(err.response.data.detail || "")) {
        this.logout("Administração desativada: defina ADMIN_EMAILS (ou ADMIN_TOKEN) no serviço jobs.");
        return true;
      }
      if (status === 429) this.error = "Muitas requisições: aumente o intervalo de atualização.";
      else if (status === 404 || status === 502) this.error = "Serviço do cluster indisponível (jobs/Kong).";
      else this.error = (err.response && err.response.data && err.response.data.detail) || err.message;
      return false;
    },
    async refresh() {
      if (!this.session) return;
      // Aba em segundo plano: não consulta o Slurm à toa
      if (document.hidden) {
        this.schedule();
        return;
      }
      this.loading = true;
      try {
        const { data } = await fetchOverview(await this.bearer());
        this.data = data;
        this.error = "";
        this.lastUpdate = Date.now();
        await this.refreshDetail();
      } catch (err) {
        if (this.handleError(err)) return;
      } finally {
        this.loading = false;
      }
      this.schedule();
    },
    openRun(runId) {
      this.detailId = runId;
      this.detail = null;
      this.log = null;
      this.detailOpen = true;
      this.refreshDetail();
    },
    async refreshDetail() {
      if (!this.detailOpen || !this.detailId || !this.session) return;
      const runId = this.detailId;
      try {
        const credential = await this.bearer();
        const [detail, log] = await Promise.all([
          fetchRun(credential, runId),
          fetchRunLog(credential, runId, this.logLines)
        ]);
        if (this.detailId !== runId) return;
        const logElement = this.$refs.log;
        const atBottom = !logElement || logElement.scrollHeight - logElement.scrollTop - logElement.clientHeight < 40;
        this.detail = detail.data;
        this.log = log.data;
        // Acompanha o fim do log enquanto o usuário não rolou para cima
        if (atBottom) {
          this.$nextTick(() => {
            if (this.$refs.log) this.$refs.log.scrollTop = this.$refs.log.scrollHeight;
          });
        }
      } catch (err) {
        this.handleError(err);
      }
    },
    hasNegativeTiming(timings) {
      return Object.values(timings || {}).some(v => v != null && v < 0);
    },
    shortId(value) {
      const text = String(value || "");
      return text.length > 10 ? `${text.slice(0, 8)}…` : text;
    },
    outputIcon(kind) {
      return {
        field: "mdi-waves",
        dielectric: "mdi-shape-outline",
        video: "mdi-movie-outline",
        spectrum: "mdi-chart-bell-curve",
        image: "mdi-image-outline",
        mesh: "mdi-cube-outline"
      }[kind] || "mdi-file-outline";
    },
    problemLabel(key) {
      return {
        unknowns: "Incógnitas",
        elements: "Elementos da malha",
        order: "Ordem usada",
        requested_order: "Ordem pedida",
        quality: "Preset",
        pml: "PML",
        fallback_reason: "Motivo da troca de ordem",
        processes: "Processos MPI"
      }[key] || key;
    },
    formatProblem(value) {
      if (typeof value === "number") return value.toLocaleString("pt-BR");
      if (typeof value === "boolean") return value ? "sim" : "não";
      return value == null ? "—" : value;
    },
    formatNumber(value, digits) {
      return typeof value === "number" && isFinite(value) ? value.toFixed(digits) : "—";
    },
    formatDuration(seconds) {
      if (seconds == null) return "—";
      const sign = seconds < 0 ? "−" : "";
      let s = Math.abs(seconds);
      if (s < 10) return `${sign}${s.toFixed(2)} s`;
      if (s < 60) return `${sign}${s.toFixed(1)} s`;
      const h = Math.floor(s / 3600);
      s -= h * 3600;
      const m = Math.floor(s / 60);
      const sec = Math.round(s - m * 60);
      return h ? `${sign}${h} h ${String(m).padStart(2, "0")} min` : `${sign}${m} min ${String(sec).padStart(2, "0")} s`;
    },
    formatClock(ms) {
      return new Date(ms).toLocaleTimeString("pt-BR");
    },
    formatDateTime(epoch) {
      if (!epoch) return "—";
      return new Date(epoch * 1000).toLocaleString("pt-BR");
    },
    formatMemory(mb) {
      if (mb == null) return "—";
      return mb >= 1024 ? `${(mb / 1024).toFixed(1)} GB` : `${mb} MB`;
    },
    formatBytes(bytes) {
      if (bytes == null) return "—";
      if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(bytes % 1048576 ? 1 : 0)} MiB`;
      if (bytes >= 1024) return `${(bytes / 1024).toFixed(bytes % 1024 ? 1 : 0)} KiB`;
      return `${bytes} B`;
    },
    formatResources(resources) {
      return Object.entries(resources || {})
        .filter(([, value]) => value != null)
        .map(([key, value]) => `${key}=${value}`)
        .join(" · ") || "—";
    },
    nodeColor(node) {
      if (!node.available) return node.state.includes("DRAIN") ? "orange darken-2" : "red darken-1";
      if (node.state.includes("ALLOC")) return "blue darken-1";
      if (node.state.includes("MIX")) return "teal darken-1";
      return "green darken-1";
    },
    slurmStateColor(state) {
      if (state === "RUNNING") return "blue darken-1";
      if (state === "PENDING") return "orange darken-2";
      return "grey darken-1";
    },
    isFinished(state) {
      return !["queued", "running"].includes(state);
    },
    askDelete(runs) {
      const finished = runs.filter(run => this.isFinished(run.state));
      if (!finished.length) return;
      this.deleteError = "";
      this.confirmDelete = { all: false, ids: finished.map(run => run.id), runs: finished };
    },
    async removeRuns() {
      const { all, ids } = this.confirmDelete;
      this.deleting = true;
      this.deleteError = "";
      try {
        const credential = await this.bearer();
        if (all) await deleteRuns(credential);
        // Uma por vez: se alguma falhar, as anteriores já saíram e a mensagem diz qual parou
        for (const id of ids) {
          await deleteRuns(credential, id);
          this.selectedRuns = this.selectedRuns.filter(run => run.id !== id);
        }
        if (all || ids.includes(this.detailId)) this.detailOpen = false;
        if (all) this.selectedRuns = [];
        this.confirmDelete = null;
        this.refresh();
      } catch (err) {
        const status = err.response && err.response.status;
        if (status === 401 || status === 403) {
          this.confirmDelete = null;
          this.handleError(err);
        } else {
          this.deleteError = (err.response && err.response.data && err.response.data.detail) || err.message;
        }
      } finally {
        this.deleting = false;
      }
    },
    runStateColor(state) {
      return {
        running: "blue darken-1",
        queued: "orange darken-2",
        completed: "green darken-1",
        cancelled: "grey darken-1"
      }[state] || "red darken-1";
    },
    onVisibility() {
      if (!document.hidden && this.session && !this.paused) this.refresh();
    }
  },
  mounted() {
    document.title = "Cluster · ElectrosFI";
    document.addEventListener("visibilitychange", this.onVisibility);
    if (this.session) this.refresh();
    else this.checkFirebaseAdmin();
  },
  beforeDestroy() {
    clearTimeout(this.timer);
    document.removeEventListener("visibilitychange", this.onVisibility);
  }
};
</script>

<style scoped>
.delete-list {
  max-height: 200px;
  overflow-y: auto;
}
.sim-cell {
  max-width: 220px;
  line-height: 1.2;
}
.interval-select {
  max-width: 90px;
}
.cpu-cell {
  min-width: 170px;
}
.log {
  background: #1e1e1e;
  color: #e0e0e0;
  font-size: 12px;
  line-height: 1.4;
  padding: 10px;
  border-radius: 4px;
  max-height: 420px;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-all;
}
.result-error {
  white-space: pre-wrap;
  font-family: monospace;
  font-size: 12px;
}
.admin-cluster >>> .clickable {
  cursor: pointer;
}
.timings span + span::before {
  content: " · ";
  color: #9e9e9e;
}
</style>
